import { FormHelperText, Theme } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import { createStyles, withStyles } from '@material-ui/core/styles';
import * as React from 'react';
import { AddressingEntity } from '../../store';

interface StylesProps {
  classes: {
    filters: string;
    form: string;
    formControl: string;
    selectEmpty: string;
    formButtons: string;
    rightMarginButton: string;
    selectOption: string;
  };
}

export interface FiltersProps {
  parentMediaId: number;

  groups: AddressingEntity[];
  selectedGroup: number;

  sites: AddressingEntity[];
  selectedSite: number;

  players: AddressingEntity[];
  selectedPlayer: number;
}

export interface FiltersActions {
  getGroupsAndSites: (channelId: number) => void;
  selectSite: (value: number) => void;
  selectGroup: (value: number) => void;
  selectPlayer: (value: number) => void;
  resetFilters: (group: number, site: number, player: number) => void;
  closeSlidePanel: () => void;
}

type AllProps = FiltersProps & FiltersActions & StylesProps;

interface FiltersState {
  isDirty: boolean;
  backupSelectedGroup: number;
  backupSelectedSite: number;
  backupSelectedPlayer: number;
  currentSelectedPlayer: number;
  currentSelectedPlayerError: string;
}

const styles = (theme: Theme) =>
  createStyles({
    filters: { width: '400px', padding: '20px' },
    form: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'stretch',
      marginTop: theme.spacing(4)
    },
    formControl: {
      marginTop: theme.spacing(),
      minWidth: 120,
      maxWidth: '400px'
    },
    selectEmpty: {
      marginTop: theme.spacing(2)
    },
    formButtons: {
      marginTop: theme.spacing(4),
      display: 'flex',
      flexDirection: 'row-reverse'
    },
    rightMarginButton: {
      marginRight: theme.spacing()
    },
    selectOption: {
      maxWidth: 500
    }
  });

class Filters extends React.PureComponent<AllProps, FiltersState> {
  constructor(props: AllProps) {
    super(props);
    this.groupValueChanged = this.groupValueChanged.bind(this);
    this.siteValueChanged = this.siteValueChanged.bind(this);
    this.state = {
      isDirty: false,
      backupSelectedGroup: this.props.selectedGroup,
      backupSelectedSite: this.props.selectedSite,
      backupSelectedPlayer: this.props.selectedPlayer,
      currentSelectedPlayer: this.props.selectedPlayer,
      currentSelectedPlayerError: ''
    };
  }

  public componentDidMount() {
    if (this.props.groups.length === 0) {
      this.props.getGroupsAndSites(this.props.parentMediaId);
    }
  }

  public static getDerivedStateFromProps(props: AllProps, state: FiltersState) {
    if (!state.isDirty && props.selectedPlayer !== state.currentSelectedPlayer) {
      return { ...state, currentSelectedPlayer: props.selectedPlayer };
    }
    return null;
  }

  private getValueFromEvent = (e: React.ChangeEvent<HTMLSelectElement>) => {
    let id = Number(e.target.value);
    if (Number.isNaN(id)) {
      id = 0;
    }
    return id;
  };

  private validatePlayer = (
    group: number,
    site: number,
    player: number,
    finalCheck: boolean = false
  ): { valid: boolean; error: string } => {
    const allGood = { valid: true, error: '' };
    if (player) {
      return allGood;
    }
    if (!group && !site) {
      return allGood;
    }
    if (this.state.currentSelectedPlayer === this.state.backupSelectedPlayer && !finalCheck) {
      return allGood;
    }
    return { valid: false, error: 'Player selection cannot be empty' };
  };

  private groupValueChanged = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const id = this.getValueFromEvent(e);
    const error = this.validatePlayer(id, this.props.selectedSite, this.state.currentSelectedPlayer).error;
    this.setState({ ...this.state, isDirty: true, currentSelectedPlayerError: error }, () =>
      this.props.selectGroup(id)
    );
  };

  private siteValueChanged = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const id = this.getValueFromEvent(e);
    const error = this.validatePlayer(this.props.selectedGroup, id, this.state.currentSelectedPlayer).error;
    this.setState({ ...this.state, isDirty: true, currentSelectedPlayerError: error }, () => this.props.selectSite(id));
  };

  private playerValueChanged = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const id = Number(e.target.value);
    const error = this.validatePlayer(this.props.selectedGroup, this.props.selectedSite, id).error;
    this.setState({ ...this.state, isDirty: true, currentSelectedPlayer: id, currentSelectedPlayerError: error });
  };

  private applyChanges = (e: React.MouseEvent<HTMLElement>) => {
    const validation = this.validatePlayer(
      this.props.selectedGroup,
      this.props.selectedSite,
      this.state.currentSelectedPlayer,
      true
    );
    if (!validation.valid) {
      this.setState({ ...this.state, currentSelectedPlayerError: validation.error });
    } else {
      this.setState({
        ...this.state,
        backupSelectedGroup: this.props.selectedGroup,
        backupSelectedSite: this.props.selectedSite,
        backupSelectedPlayer: this.state.currentSelectedPlayer,
        isDirty: false
      });
      this.props.selectPlayer(this.state.currentSelectedPlayer);
      this.props.closeSlidePanel();
    }
  };

  private cancel = (e: React.MouseEvent<HTMLElement>) => {
    this.props.resetFilters(
      this.state.backupSelectedGroup,
      this.state.backupSelectedSite,
      this.state.backupSelectedPlayer
    );
    this.setState({
      ...this.state,
      isDirty: false,
      currentSelectedPlayer: this.state.backupSelectedPlayer,
      currentSelectedPlayerError: ''
    });
  };

  public render() {
    return (
      <div className={this.props.classes.filters}>
        <form className={this.props.classes.form}>
          <FormControl className={this.props.classes.formControl}>
            <InputLabel htmlFor="addressing-group">Group</InputLabel>
            <Select
              value={this.props.selectedGroup ? this.props.selectedGroup.toString() : ''}
              onChange={this.groupValueChanged}
              inputProps={{
                name: 'Group',
                id: 'addressing-group',
                placeholder: 'Group'
              }}
            >
              {[
                <MenuItem key="none" value="none">
                  <em>None</em>
                </MenuItem>
              ].concat(
                this.props.groups
                  .filter(g => !g.hide)
                  .map(g => (
                    <MenuItem key={g.id} value={g.id} className={this.props.classes.selectOption}>
                      <div style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{g.name}</div>
                    </MenuItem>
                  ))
              )}
            </Select>
          </FormControl>
          <FormControl className={this.props.classes.formControl}>
            <InputLabel htmlFor="addressing-site">Site</InputLabel>
            <Select
              value={this.props.selectedSite ? this.props.selectedSite.toString() : ''}
              onChange={this.siteValueChanged}
              inputProps={{
                name: 'Site',
                id: 'addressing-site'
              }}
            >
              {[
                <MenuItem key="none" value={0}>
                  <em>None</em>
                </MenuItem>
              ].concat(
                this.props.sites.map(g => (
                  <MenuItem key={g.id} value={g.id} className={this.props.classes.selectOption}>
                    <div style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{g.name}</div>
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
          <FormControl className={this.props.classes.formControl} error={this.state.currentSelectedPlayerError !== ''}>
            <InputLabel htmlFor="addressing-player">Player</InputLabel>
            <Select
              value={this.state.currentSelectedPlayer ? this.state.currentSelectedPlayer.toString() : ''}
              onChange={this.playerValueChanged}
              inputProps={{
                name: 'Player',
                id: 'addressing-player'
              }}
            >
              {[
                <MenuItem key="none" value={0}>
                  <em>None</em>
                </MenuItem>
              ].concat(
                this.props.players.map(g => (
                  <MenuItem key={g.id} value={g.id} className={this.props.classes.selectOption}>
                    <div style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{g.name}</div>
                  </MenuItem>
                ))
              )}
            </Select>
            {this.state.currentSelectedPlayerError !== '' && (
              <FormHelperText>{this.state.currentSelectedPlayerError}</FormHelperText>
            )}
          </FormControl>
        </form>
        <div className={this.props.classes.formButtons}>
          <Button size="large" variant="contained" color="primary" onClick={this.applyChanges}>
            Select player
          </Button>
          {this.state.isDirty && (
            <Button size="large" className={this.props.classes.rightMarginButton} onClick={this.cancel}>
              Cancel
            </Button>
          )}
        </div>
      </div>
    );
  }
}

export default withStyles(styles)(Filters);



// WEBPACK FOOTER //
// ./src/components/filters/Filters.tsx